import React, { useState, useEffect } from "react";
import { Modal, Form, Spinner } from "react-bootstrap";
import apiClient from "../../utils/apiClient";
import { useBranch } from "../../contexts/BranchContext";
import { AppButton } from "../ui";

/**
 * TransferBranchModal — Transfer enquiries or students to a different branch.
 *
 * Props:
 *   show        — Boolean
 *   onHide      — close handler
 *   selectedIds — Array of IDs to transfer
 *   mode        — "enquiries" | "students"
 *   onTransfer  — callback on success
 */
const TransferBranchModal = ({ show, onHide, selectedIds = [], mode = "enquiries", onTransfer }) => {
  const { branches } = useBranch();
  const [targetBranchId, setTargetBranchId] = useState("");
  const [targetBatchId, setTargetBatchId] = useState("");
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Reset on open
  useEffect(() => {
    if (show) {
      setTargetBranchId("");
      setTargetBatchId("");
      setBatches([]);
      setError("");
    }
  }, [show]);

  // Fetch batches in target branch (for student transfer)
  useEffect(() => {
    if (mode === "students" && targetBranchId) {
      apiClient
        .get("/batches/list", {
          params: { page: 1, pageSize: 200 },
          headers: { "X-Branch-Id": targetBranchId },
        })
        .then(({ data }) => setBatches(data.batches || []))
        .catch(() => setBatches([]));
    } else {
      setBatches([]);
    }
    setTargetBatchId("");
  }, [targetBranchId, mode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      if (mode === "enquiries") {
        await apiClient.post("/branches/transfer-enquiries", {
          enquiryIds: selectedIds,
          targetBranchId,
        });
      } else {
        await apiClient.post("/branches/transfer-students", {
          studentIds: selectedIds,
          targetBranchId,
          targetBatchId: targetBatchId || undefined,
        });
      }
      if (onTransfer) onTransfer();
      onHide();
    } catch (err) {
      setError(err?.response?.data?.message || "Transfer failed.");
    } finally {
      setLoading(false);
    }
  };

  const activeBranches = branches.filter((b) => b.isActive);
  const title = mode === "enquiries" ? "Transfer Enquiries" : "Transfer Students";
  const countLabel = `${selectedIds.length} ${mode === "enquiries" ? "enquiry(ies)" : "student(s)"}`;

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title className="fs-6 fw-semibold">{title}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <p className="text-muted small mb-3">
            Move <strong>{countLabel}</strong> to a different branch.
          </p>

          {error && <div className="alert alert-danger py-2 small">{error}</div>}

          <Form.Group className="mb-3">
            <Form.Label className="small fw-medium">Target Branch</Form.Label>
            <Form.Select
              value={targetBranchId}
              onChange={(e) => setTargetBranchId(e.target.value)}
              required
            >
              <option value="">Select branch</option>
              {activeBranches.map((b) => (
                <option key={b._id} value={b._id}>
                  {b.name} {b.isDefault ? "(Default)" : ""}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          {mode === "students" && targetBranchId && (
            <Form.Group>
              <Form.Label className="small fw-medium">Target Batch (optional)</Form.Label>
              <Form.Select
                value={targetBatchId}
                onChange={(e) => setTargetBatchId(e.target.value)}
              >
                <option value="">Keep current batch</option>
                {batches.map((b) => (
                  <option key={b._id} value={b._id}>
                    {b.name}
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted" style={{ fontSize: "0.7rem" }}>
                Optionally move students into a batch in the target branch.
              </Form.Text>
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <AppButton variant="outline" size="sm" onClick={onHide} disabled={loading}>
            Cancel
          </AppButton>
          <AppButton type="submit" size="sm" disabled={!targetBranchId || loading}>
            {loading ? <Spinner size="sm" /> : "Transfer"}
          </AppButton>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default TransferBranchModal;
